import type { Ref, ComputedRef } from 'vue'

declare global {
  interface Document {
    readonly webkitExitFullscreen: () => Promise<void>
    readonly msExitFullscreen: () => Promise<void>
    readonly mozCancelFullScreen: () => Promise<void>
    readonly mozFullScreenElement: Element
    readonly webkitFullscreenElement: Element
    readonly msFullscreenElement: Element
    readonly mozFullScreenEnabled: boolean
    readonly msFullscreenEnabled: boolean
    readonly webkitFullscreenEnabled: boolean
    onmozfullscreenchange: (event: Event) => void
    onwebkitfullscreenchange: (event: Event) => void
    onmsfullscreenchange: (event: Event) => void
  }

  interface HTMLElement {
    readonly webkitRequestFullScreen: () => Promise<void>
    readonly mozRequestFullScreen: () => Promise<void>
    readonly msRequestFullscreen: () => Promise<void>
  }
}

export function useFullscreenHelpers({ deck }: { deck: any }) {
  const { $analytics } = useNuxtApp()

  const _isCurrentlyFullscreen: Ref<boolean> = ref(false)
  const isCurrentlyFullscreen = computed({
    get() {
      return (
        !!document.fullscreenElement ||
        !!document.mozFullScreenElement ||
        !!document.webkitFullscreenElement ||
        !!document.msFullscreenElement
      )
    },
    set(value) {
      _isCurrentlyFullscreen.value = value
    },
  })

  const isFullscreenEnabled: ComputedRef<boolean> = computed(
    () => document.fullscreenEnabled || document.mozFullScreenEnabled || document.webkitFullscreenEnabled || document.msFullscreenEnabled,
  )

  onMounted(() => {
    document.onfullscreenchange = handleFullscreenChange
    document.onmozfullscreenchange = handleFullscreenChange
    document.onwebkitfullscreenchange = handleFullscreenChange
    document.onmsfullscreenchange = handleFullscreenChange
  })

  function handleFullscreenChange() {
    if (!deck.value) return

    isCurrentlyFullscreen.value
      ? $analytics.track('embedDeckExitFullscreen', {
          deckId: deck.value.id,
          deckName: deck.value.title,
          companyId: deck.value.company.id,
          companyName: deck.value.company.name,
        })
      : $analytics.track('embedDeckEnterFullscreen', {
          deckId: deck.value.id,
          deckName: deck.value.title,
          companyId: deck.value.company.id,
          companyName: deck.value.company.name,
        })

    _isCurrentlyFullscreen.value =
      !!document.fullscreenElement ||
      !!document.mozFullScreenElement ||
      !!document.webkitFullscreenElement ||
      !!document.msFullscreenElement
  }

  function cancelFullScreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen()
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen()
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen.call(document)
    }
  }

  function toggleFullscreen() {
    if (
      !!document.fullscreenElement ||
      !!document.mozFullScreenElement ||
      !!document.webkitFullscreenElement ||
      !!document.msFullscreenElement
    ) {
      cancelFullScreen()
    } else {
      requestFullscreen(document.documentElement)
    }
  }

  function requestFullscreen(element: HTMLElement) {
    if (element.requestFullscreen) {
      try {
        element.requestFullscreen()
      } catch (e) {
        throw e
      }
    } else if (element.mozRequestFullScreen) {
      try {
        element.mozRequestFullScreen()
      } catch (e) {
        throw e
      }
    } else if (element.webkitRequestFullScreen) {
      try {
        element.webkitRequestFullScreen()
      } catch (e) {
        throw e
      }
    } else if (element.msRequestFullscreen) {
      try {
        element.msRequestFullscreen.call(document.body)
      } catch (e) {
        throw e
      }
    }
  }

  return {
    isCurrentlyFullscreen,
    isFullscreenEnabled,
    toggleFullscreen,
  }
}
